import { motion } from "framer-motion";
import { UserCheck, Phone, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuote } from "@/context/QuoteContext";
import { SITE, IMAGES, STATS } from "@/data/site";

export const OwnerTrust = () => {
  const { openQuote } = useQuote();

  return (
    <section id="why-us" data-testid="owner-trust-section" className="py-16 sm:py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative"
        >
          <div className="rounded-3xl overflow-hidden shadow-2xl">
            <img src={IMAGES.ownerTrust} alt="Owner personally verifying a job site" className="w-full h-[460px] object-cover" />
          </div>
          <div className="absolute -bottom-6 -right-2 sm:right-6 bg-slate-900 text-white rounded-2xl p-5 shadow-xl max-w-[230px]">
            <div className="flex items-center gap-2 text-red-600">
              <UserCheck className="h-6 w-6" />
              <span className="font-display font-extrabold text-2xl">100%</span>
            </div>
            <p className="mt-1 text-sm text-slate-300 font-medium">Owner-verified jobs, every single one, in person.</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-sm uppercase tracking-[0.2em] font-bold text-red-700">The Templar Armor Difference</span>
          <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-950">
            The Owner Is On Your Roof, Not Just On Paper
          </h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Big companies send crews and disappear. At Templar Armor, the owner is personally there to help and
            <span className="font-semibold text-slate-800"> verifies every job in person</span> to guarantee the
            best service possible. That's how we've stayed one of California's most reliable construction companies
            since 1980.
          </p>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">
            <span className="font-display font-bold text-slate-900 italic">"{SITE.motto}"</span>
          </p>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {STATS.map((s) => (
              <div key={s.label} className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-center">
                <div className="font-display font-extrabold text-2xl text-red-700">{s.value}</div>
                <div className="text-xs font-semibold text-slate-500 mt-1 uppercase tracking-wide">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button
              data-testid="owner-quote-btn"
              onClick={() => openQuote()}
              className="bg-red-700 hover:bg-red-800 text-white font-bold rounded-full h-12 px-7 hover:scale-105 transition-transform"
            >
              Start My Free Quote <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
            <a href={SITE.phoneHref} data-testid="owner-call-btn">
              <Button variant="outline" className="w-full sm:w-auto border-slate-300 text-slate-900 hover:bg-slate-900 hover:text-white font-bold rounded-full h-12 px-7">
                <Phone className="mr-1 h-4 w-4" /> {SITE.phone}
              </Button>
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
